import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import styled from 'styled-components';

import { FiatCurrency } from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { range } from '@sorare/core/src/lib/arrays';
import { qualityNames } from '@sorare/core/src/lib/players';
import type { SimpleReward } from '@sorare/core/src/lib/rewards';
import { RewardType } from '@sorare/core/src/lib/rewards';

import { Variant } from '../types';

const Stars = styled(Horizontal).attrs({ as: 'span', gap: 0 })`
  height: 14px;
  gap: 1px;
`;

type Props = {
  variant: Variant;
  simpleReward: SimpleReward;
};

export const RewardOverviewTitle = ({ variant, simpleReward }: Props) => {
  const { formatNumber } = useIntl();
  const { count, type, quality } = simpleReward;

  const formattedCount = formatNumber(count, {
    maximumFractionDigits: 1,
    notation: 'compact',
    compactDisplay: 'short',
  });

  if (
    type === RewardType.PROBABILISTIC_BUNDLE &&
    simpleReward.boxConfig?.stars
  ) {
    if (variant === Variant.INLINE) {
      return `${count > 1 ? `x${formattedCount} ` : ''}${simpleReward.boxConfig.stars} Star`;
    }
    return (
      <Stars>
        {range(simpleReward.boxConfig.stars).map(i => (
          <FontAwesomeIcon
            key={i}
            icon={faStar}
            color="var(--c-yellow-400)"
            width={8}
          />
        ))}
      </Stars>
    );
  }

  switch (type) {
    case RewardType.CASH:
      return (
        <FormattedNumber
          value={count}
          style="currency"
          currency={FiatCurrency.USD}
          currencyDisplay="narrowSymbol"
          maximumFractionDigits={0}
        />
      );
    case RewardType.CREDIT:
      return (
        <>
          <FormattedNumber
            value={count}
            style="currency"
            currency={FiatCurrency.USD}
            currencyDisplay="narrowSymbol"
            maximumFractionDigits={0}
          />{' '}
          <FormattedMessage
            id="RewardOverviewTitle.inCredit"
            defaultMessage="in credits"
          />
        </>
      );
    default:
      if (type === RewardType.CARD && quality) {
        return `${count > 1 ? `x${formattedCount} ` : ''}${qualityNames[quality]}`;
      }
      return `x${formattedCount}`;
  }
};
